/**
 * Super Admin knowledge base service.
 *
 * CRUD for categories, tags, and articles. Uses top-level Firestore collections
 * kbCategories, kbTags, kbArticles. Validates languageSettings before write.
 */ 

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import type { UpdateData, DocumentData } from 'firebase/firestore';
import { db, auth } from '@municipality/firebase';
import {
  KB_COLLECTIONS,
  KBCategory,
  KBTag,
  KBArticle,
  DEFAULT_LANGUAGE_SETTINGS,
  SCREENSHOT_CAPTION_MAX_LENGTH,
  type KBCategoryTranslation,
  type KBTagTranslation,
  type KBArticleTranslation,
  type KBScreenshot,
} from '@municipality/types';
import {
  validateLanguageSettings,
  sanitizeLanguageSettings,
} from '@municipality/types';

function toDate(
  value: Timestamp | Date | string | undefined
): Date | undefined {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value === 'object' && 'toDate' in value) {
    return (value as Timestamp).toDate();
  }
  if (typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

function toFirestoreTimestamp(date: Date): Timestamp {
  return Timestamp.fromDate(date);
}

function mapCategoryDoc(
  data: Record<string, unknown>,
  id: string
): KBCategory {
  const createdAt = toDate(data.createdAt as Timestamp | undefined) ?? new Date();
  const updatedAt = toDate(data.updatedAt as Timestamp | undefined) ?? new Date();
  return {
    id,
    translations: (data.translations as Record<string, KBCategoryTranslation>) ?? {},
    languageSettings:
      (data.languageSettings as KBCategory['languageSettings']) ??
      DEFAULT_LANGUAGE_SETTINGS,
    order: typeof data.order === 'number' ? data.order : 0,
    isActive: data.isActive === true,
    showOnLanding: data.showOnLanding === true,
    createdAt,
    updatedAt,
  };
}

function mapTagDoc(data: Record<string, unknown>, id: string): KBTag {
  const createdAt = toDate(data.createdAt as Timestamp | undefined) ?? new Date();
  const updatedAt = toDate(data.updatedAt as Timestamp | undefined) ?? new Date();
  return {
    id,
    translations: (data.translations as Record<string, KBTagTranslation>) ?? {},
    languageSettings:
      (data.languageSettings as KBTag['languageSettings']) ??
      DEFAULT_LANGUAGE_SETTINGS,
    order: typeof data.order === 'number' ? data.order : 0,
    isActive: data.isActive === true,
    createdAt,
    updatedAt,
  };
}

function mapArticleDoc(data: Record<string, unknown>, id: string): KBArticle {
  const createdAt = toDate(data.createdAt as Timestamp | undefined) ?? new Date();
  const updatedAt = toDate(data.updatedAt as Timestamp | undefined) ?? new Date();
  const publishedAt = toDate(data.publishedAt as Timestamp | undefined);
  return {
    id,
    categoryId: (data.categoryId as string) ?? '',
    tagIds: Array.isArray(data.tagIds) ? (data.tagIds as string[]) : [],
    relatedArticleIds: Array.isArray(data.relatedArticleIds)
      ? (data.relatedArticleIds as string[])
      : [],
    translations: (data.translations as Record<string, KBArticleTranslation>) ?? {},
    languageSettings:
      (data.languageSettings as KBArticle['languageSettings']) ??
      DEFAULT_LANGUAGE_SETTINGS,
    screenshots: Array.isArray(data.screenshots)
      ? (data.screenshots as KBScreenshot[])
      : [],
    slug: (data.slug as string) ?? '',
    order: typeof data.order === 'number' ? data.order : 0,
    status: data.status === 'published' ? 'published' : 'draft',
    authorId: (data.authorId as string) ?? '',
    authorName: (data.authorName as string) ?? '',
    createdAt,
    updatedAt,
    publishedAt,
  };
}

function normalizeScreenshots(screenshots: KBScreenshot[]): KBScreenshot[] {
  return screenshots.map((s) => ({
    ...s,
    caption: s.caption ? s.caption.slice(0, SCREENSHOT_CAPTION_MAX_LENGTH) : undefined,
  }));
}

function prepareLanguageSettings(
  input: KBCategory['languageSettings'] | undefined
): KBCategory['languageSettings'] {
  const result = validateLanguageSettings(input);
  if (!result.valid) {
    throw new Error(`Invalid language settings: ${result.errors.join(', ')}`);
  }
  return sanitizeLanguageSettings(input);
}

// ==================== CATEGORIES ====================

export interface CreateCategoryData {
  translations: Record<string, KBCategoryTranslation>;
  languageSettings: KBCategory['languageSettings'];
  order?: number;
  isActive?: boolean;
  showOnLanding?: boolean;
}

export async function createCategory(
  data: CreateCategoryData
): Promise<KBCategory> {
  const languageSettings = prepareLanguageSettings(data.languageSettings);
  const now = new Date();
  const docData = {
    translations: data.translations,
    languageSettings,
    order: data.order ?? 0,
    isActive: data.isActive ?? true,
    showOnLanding: data.showOnLanding ?? false,
    createdAt: toFirestoreTimestamp(now),
    updatedAt: toFirestoreTimestamp(now),
  };
  const ref = await addDoc(
    collection(db, KB_COLLECTIONS.categories),
    docData
  );
  return mapCategoryDoc(docData as unknown as Record<string, unknown>, ref.id);
}

export interface UpdateCategoryData {
  translations?: Record<string, KBCategoryTranslation>;
  languageSettings?: KBCategory['languageSettings'];
  order?: number;
  isActive?: boolean;
  showOnLanding?: boolean;
}

export async function updateCategory(
  id: string,
  data: UpdateCategoryData
): Promise<void> {
  const updateData: Record<string, unknown> = {
    updatedAt: toFirestoreTimestamp(new Date()),
  };
  if (data.translations !== undefined) updateData.translations = data.translations;
  if (data.languageSettings !== undefined) {
    updateData.languageSettings = prepareLanguageSettings(data.languageSettings);
  }
  if (data.order !== undefined) updateData.order = data.order;
  if (data.isActive !== undefined) updateData.isActive = data.isActive;
  if (data.showOnLanding !== undefined)
    updateData.showOnLanding = data.showOnLanding;

  await updateDoc(
    doc(db, KB_COLLECTIONS.categories, id),
    updateData as UpdateData<DocumentData>
  );
}

export async function deleteCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, KB_COLLECTIONS.categories, id));
}

/** Fetch all categories (including inactive) ordered by order. */
export async function getCategories(): Promise<KBCategory[]> {
  const q = query(
    collection(db, KB_COLLECTIONS.categories),
    orderBy('order', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) =>
    mapCategoryDoc(d.data() as Record<string, unknown>, d.id)
  );
}

export async function updateCategoryOrder(orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const ref = doc(db, KB_COLLECTIONS.categories, orderedIds[i]);
    batch.update(ref, {
      order: i,
      updatedAt: toFirestoreTimestamp(new Date()),
    });
  }
  await batch.commit();
}

// ==================== TAGS ====================

export interface CreateTagData {
  translations: Record<string, KBTagTranslation>;
  languageSettings: KBTag['languageSettings'];
  order?: number;
  isActive?: boolean;
}

export async function createTag(data: CreateTagData): Promise<KBTag> {
  const languageSettings = prepareLanguageSettings(data.languageSettings);
  const now = new Date();
  const docData = {
    translations: data.translations,
    languageSettings,
    order: data.order ?? 0,
    isActive: data.isActive ?? true,
    createdAt: toFirestoreTimestamp(now),
    updatedAt: toFirestoreTimestamp(now),
  };
  const ref = await addDoc(collection(db, KB_COLLECTIONS.tags), docData);
  return mapTagDoc(docData as unknown as Record<string, unknown>, ref.id);
}

export interface UpdateTagData {
  translations?: Record<string, KBTagTranslation>;
  languageSettings?: KBTag['languageSettings'];
  order?: number;
  isActive?: boolean;
}

export async function updateTag(id: string, data: UpdateTagData): Promise<void> {
  const updateData: Record<string, unknown> = {
    updatedAt: toFirestoreTimestamp(new Date()),
  };
  if (data.translations !== undefined) updateData.translations = data.translations;
  if (data.languageSettings !== undefined) {
    updateData.languageSettings = prepareLanguageSettings(data.languageSettings);
  }
  if (data.order !== undefined) updateData.order = data.order;
  if (data.isActive !== undefined) updateData.isActive = data.isActive;

  await updateDoc(
    doc(db, KB_COLLECTIONS.tags, id),
    updateData as UpdateData<DocumentData>
  );
}

export async function deleteTag(id: string): Promise<void> {
  await deleteDoc(doc(db, KB_COLLECTIONS.tags, id));
}

/** Fetch all tags (including inactive) ordered by order. */
export async function getTags(): Promise<KBTag[]> {
  const q = query(
    collection(db, KB_COLLECTIONS.tags),
    orderBy('order', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) =>
    mapTagDoc(d.data() as Record<string, unknown>, d.id)
  );
}

export async function updateTagOrder(orderedIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const ref = doc(db, KB_COLLECTIONS.tags, orderedIds[i]);
    batch.update(ref, {
      order: i,
      updatedAt: toFirestoreTimestamp(new Date()),
    });
  }
  await batch.commit();
}

// ==================== ARTICLES ====================

export interface CreateArticleData {
  categoryId: string;
  tagIds?: string[];
  relatedArticleIds?: string[];
  translations: Record<string, KBArticleTranslation>;
  languageSettings: KBArticle['languageSettings'];
  screenshots?: KBScreenshot[];
  slug?: string;
  order?: number;
  status?: 'draft' | 'published';
}

export async function createArticle(
  data: CreateArticleData
): Promise<KBArticle> {
  const categorySnap = await getDoc(doc(db, KB_COLLECTIONS.categories, data.categoryId));
  if (!categorySnap.exists()) {
    throw new Error('Category not found');
  }
  const languageSettings = prepareLanguageSettings(data.languageSettings);
  const currentUser = auth.currentUser;
  const now = new Date();
  const docData: Record<string, unknown> = {
    categoryId: data.categoryId,
    tagIds: data.tagIds ?? [],
    relatedArticleIds: data.relatedArticleIds ?? [],
    translations: data.translations,
    languageSettings,
    screenshots: normalizeScreenshots(data.screenshots ?? []),
    slug: data.slug ?? '',
    order: data.order ?? 0,
    status: data.status ?? 'draft',
    authorId: currentUser?.uid ?? '',
    authorName: currentUser?.displayName ?? currentUser?.email ?? '',
    createdAt: toFirestoreTimestamp(now),
    updatedAt: toFirestoreTimestamp(now),
  };
  if (data.status === 'published') {
    docData.publishedAt = toFirestoreTimestamp(now);
  }
  const ref = await addDoc(
    collection(db, KB_COLLECTIONS.articles),
    docData
  );
  return mapArticleDoc(docData as unknown as Record<string, unknown>, ref.id);
}

export interface UpdateArticleData {
  categoryId?: string;
  tagIds?: string[];
  relatedArticleIds?: string[];
  translations?: Record<string, KBArticleTranslation>;
  languageSettings?: KBArticle['languageSettings'];
  screenshots?: KBScreenshot[];
  slug?: string;
  order?: number;
  status?: 'draft' | 'published';
}

export async function updateArticle(
  id: string,
  data: UpdateArticleData
): Promise<void> {
  if (data.categoryId !== undefined) {
    const categorySnap = await getDoc(doc(db, KB_COLLECTIONS.categories, data.categoryId));
    if (!categorySnap.exists()) {
      throw new Error('Category not found');
    }
  }
  const updateData: Record<string, unknown> = {
    updatedAt: toFirestoreTimestamp(new Date()),
  };
  if (data.categoryId !== undefined) updateData.categoryId = data.categoryId;
  if (data.tagIds !== undefined) updateData.tagIds = data.tagIds;
  if (data.relatedArticleIds !== undefined)
    updateData.relatedArticleIds = data.relatedArticleIds;
  if (data.translations !== undefined) updateData.translations = data.translations;
  if (data.languageSettings !== undefined) {
    updateData.languageSettings = prepareLanguageSettings(data.languageSettings);
  }
  if (data.screenshots !== undefined) updateData.screenshots = normalizeScreenshots(data.screenshots);
  if (data.slug !== undefined) updateData.slug = data.slug;
  if (data.order !== undefined) updateData.order = data.order;
  if (data.status !== undefined) {
    updateData.status = data.status;
    if (data.status === 'published') {
      const existing = await getDoc(doc(db, KB_COLLECTIONS.articles, id));
      if (existing.exists() && !existing.data()?.publishedAt) {
        updateData.publishedAt = toFirestoreTimestamp(new Date());
      }
    }
  }

  await updateDoc(
    doc(db, KB_COLLECTIONS.articles, id),
    updateData as UpdateData<DocumentData>
  );
}

export async function deleteArticle(id: string): Promise<void> {
  await deleteDoc(doc(db, KB_COLLECTIONS.articles, id));
}

/** Fetch all articles (any status), optionally filtered by category or status. Uses existing indexes. */
export async function getArticles(options?: {
  categoryId?: string;
  status?: 'draft' | 'published';
  maxResults?: number;
}): Promise<KBArticle[]> {
  const coll = collection(db, KB_COLLECTIONS.articles);
  const maxResults = options?.maxResults ?? 200;
  let q;
  if (options?.categoryId && options?.status) {
    q = query(
      coll,
      where('categoryId', '==', options.categoryId),
      where('status', '==', options.status),
      orderBy('order', 'asc'),
      limit(maxResults)
    );
  } else if (options?.categoryId) {
    q = query(
      coll,
      where('categoryId', '==', options.categoryId),
      orderBy('order', 'asc'),
      limit(maxResults)
    );
  } else if (options?.status) {
    q = query(
      coll,
      where('status', '==', options.status),
      orderBy('order', 'asc'),
      limit(maxResults)
    );
  } else {
    q = query(coll, orderBy('order', 'asc'), limit(maxResults));
  }
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) =>
    mapArticleDoc(d.data() as Record<string, unknown>, d.id)
  );
}

export async function getArticleById(id: string): Promise<KBArticle | null> {
  const snap = await getDoc(doc(db, KB_COLLECTIONS.articles, id));
  if (!snap.exists()) return null;
  return mapArticleDoc(snap.data() as Record<string, unknown>, snap.id);
}

/**
 * Check if an article with the given slug exists (for uniqueness validation).
 * Excludes the given article ID when updating.
 */
export async function getArticleBySlugForValidation(
  slug: string,
  excludeArticleId?: string
): Promise<KBArticle | null> {
  if (!slug?.trim()) return null;
  const q = query(
    collection(db, KB_COLLECTIONS.articles),
    where('slug', '==', slug),
    limit(10)
  );
  const snapshot = await getDocs(q);
  const match = snapshot.docs.find(
    (d) => !excludeArticleId || d.id !== excludeArticleId
  );
  if (!match) return null;
  return mapArticleDoc(match.data() as Record<string, unknown>, match.id);
}

export async function updateArticleOrder(
  categoryId: string,
  orderedIds: string[]
): Promise<void> {
  if (orderedIds.length === 0) return;

  const articles = await Promise.all(
    orderedIds.map((articleId) =>
      getDoc(doc(db, KB_COLLECTIONS.articles, articleId))
    )
  );

  for (let i = 0; i < articles.length; i++) {
    const snap = articles[i];
    if (!snap.exists()) {
      throw new Error(`Article ${orderedIds[i]} not found`);
    }
    const docCategoryId = (snap.data() as { categoryId?: string })?.categoryId;
    if (docCategoryId !== categoryId) {
      throw new Error(
        `Article ${orderedIds[i]} does not belong to category ${categoryId}`
      );
    }
  }

  const batch = writeBatch(db);
  for (let i = 0; i < orderedIds.length; i++) {
    const ref = doc(db, KB_COLLECTIONS.articles, orderedIds[i]);
    batch.update(ref, {
      order: i,
      categoryId,
      updatedAt: toFirestoreTimestamp(new Date()),
    });
  }
  await batch.commit();
}
